import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import Card from 'chameleon/ui-stack/ui/card';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import { getTopicQuestionsFilter } from 'legacy/packages/unit-outline/src/components/_shared/util.js';
import ResourceTypes from 'legacy/packages/unit-outline/src/constants';
import { getSignedUnitResource } from 'legacy/src/utility/utils';
import { AP_COMPUTER_SCIENCE_PRINCIPLES } from 'chameleon-src/constants/subjects';

const resourceTypeToIconMap = {
  [ResourceTypes.URL]: 'unit_guide',
  [ResourceTypes.QUESTION_BANK]: 'bulb',
  [ResourceTypes.ASSESSMENT]: 'progress_checks',
};

const resourceTypeToTextMap = {
  [ResourceTypes.URL]: {
    header: 'Guide',
    contentText: 'Download PDF',
  },
  [ResourceTypes.QUESTION_BANK]: {
    header: 'Topic Questions',
    contentText: 'View All',
  },
  [ResourceTypes.ASSESSMENT]: {
    header: 'Progress Checks',
    contentText: 'View All',
  },
};

const resourceTypeToClassNameMap = {
  [ResourceTypes.URL]: 'bg-blue-200',
  [ResourceTypes.QUESTION_BANK]: 'bg-yellow-400',
  [ResourceTypes.ASSESSMENT]: 'bg-blue-200 text-blue-200',
};

const UnitCard = (props) => {
  const {
    resourceType,
    urlTo,
    unitId,
    unitsLabel,
    widgetTagColumn,
    subjectId,
    unitTitle,
    queryType,
    displayName,
    metadata,
  } = props;
  const isCspSubject =
    parseInt(subjectId, 10) === AP_COMPUTER_SCIENCE_PRINCIPLES;
  const isUrlResource = ResourceTypes.URL === resourceType;
  const isTopicQuestionResources = ResourceTypes.QUESTION_BANK === resourceType;
  let topicQuestionsFilter = null;
  // To not render topic questions for ap seminar.
  if (isUrlResource && parseInt(subjectId, 10) === 94) {
    return null;
  }
  const [url, setUrl] = useState(urlTo);
  useEffect(() => {
    formatAndSetUrl(urlTo, resourceType);
  }, []);
  const iconName = resourceTypeToIconMap[resourceType];
  const resourceTextData = resourceTypeToTextMap[resourceType];
  const classNames = resourceTypeToClassNameMap[resourceType];
  if (isTopicQuestionResources) {
    topicQuestionsFilter = getTopicQuestionsFilter({
      subjectId: parseInt(subjectId, 10),
      unitNumber: unitId,
      metadata,
      widgetTagColumn,
      showMainTQFiltersOnly: true,
      unitTitle,
      unitsLabel,
    });
  }
  const headerText = isUrlResource
    ? `${displayName} ${resourceTextData.header} `
    : resourceTextData.header;
  const formatAndSetUrl = async (unSignedurl, resourceType = '') => {
    if (resourceType === ResourceTypes.URL) {
      const signedUrl = await getSignedUnitResource(unSignedurl);
      setUrl(signedUrl);
    } else {
      const _query = {};
      if (queryType) {
        _query.type = queryType;
      }
      if (unitId) {
        _query.unit = unitId;
      }
      let locationState = {
        topicQuestionsFilter,
        unitNumber: !isCspSubject ? unitId : null,
        questionBankPath: '/questions',
      };
      if (isCspSubject) {
        locationState.bigIdea = unitId;
      }

      setUrl({
        pathname: url,
        query: _query,
        state: locationState,
      });
    }
  };
  return (
    <Card classNames="border-2 border-gray-400">
      <Link
        className="grid grid-flow-col auto-cols-max gap-x-4 items-center h-30 w-88.2 cursor-pointer"
        to={url}
        target={isUrlResource ? '_blank' : ''}
        aria-label={`${headerText} ${resourceTextData.contentText}`}
        tabIndex="0"
      >
        <div
          className={`grid justify-center items-center w-25 rounded-lg ml-2 ${classNames}`}
        >
          <Icon
            id={iconName}
            width="64"
            height="64"
            accessibility={{ ariaHidden: true }}
          />
        </div>
        <div className="grid gap-y-2 text-16 text-black font-medium">
          {headerText}
          <span className="text-14 font-normal text-gray-600">
            {resourceTextData.contentText}
          </span>
        </div>
      </Link>
    </Card>
  );
};

UnitCard.displayName = 'UnitCard';
UnitCard.propTypes = {
  unitId: PropTypes.string.isRequired,
  unitsLabel: PropTypes.string,
  widgetTagColumn: PropTypes.string,
  subjectId: PropTypes.string,
  unitTitle: PropTypes.string.isRequired,
  resourceType: PropTypes.string.isRequired,
  urlTo: PropTypes.string.isRequired,
  queryType: PropTypes.string,
  displayName: PropTypes.string,
  metadata: PropTypes.string,
};

UnitCard.defaultProps = {
  unitsLabel: '',
  widgetTagColumn: '',
  subjectId: '',
  queryType: '',
  displayName: '',
  metadata: '[]',
};

export default UnitCard;
